import { spacing, tabletQuery } from '@globals/themes/themeConstants'
import { TenantMember, getTenantMember } from '@kudos/http-client'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import UserAvatar from '@shared/avatar/UserAvatar'
import { getUserDisplayName } from '@shared/userDisplay/UserNameDisplay'
import { useT } from '@transifex/react'
import { useQuery } from '@tanstack/react-query'

const popperStyle: SxProps = { zIndex: 3, position: 'absolute', borderRadius: spacing(2) }

type MentionsPopperProps = {
  tenantMemberId: string
  anchorEl: HTMLElement | null
  open: boolean
}

const MentionsPopper: React.FC<MentionsPopperProps> = ({ tenantMemberId, anchorEl, open }) => {
  const t = useT()
  const {
    data: tenantMember,
    isLoading,
    isFetching,
  } = useQuery<TenantMember>({
    queryKey: ['getMentionedTenantMember', tenantMemberId],
    queryFn: () => getTenantMember(tenantMemberId, { embeds: ['avatar', 'publicStaffProfile'] }),
    staleTime: 60 * 60 * 1000, // Prevent frequent api calls
  })

  const isTablet = useMediaQuery(tabletQuery)

  const { avatarUrls, firstName, lastName, publicStaffProfile } = tenantMember ?? {}
  const fullName = getUserDisplayName(tenantMember, t)

  if (!open) {
    return
  }

  if (isLoading || isFetching) {
    return (
      <Popper open={open} anchorEl={anchorEl} placement="top" sx={popperStyle}>
        <CircularProgress size={24} />
      </Popper>
    )
  }

  return (
    <Popper open={open} anchorEl={anchorEl} placement="top" sx={popperStyle}>
      <Box component={Paper} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack useFlexGap spacing={2} direction="row" alignItems="center">
            <UserAvatar media={avatarUrls} firstName={firstName} lastName={lastName} size={36} />
            <Typography variant="body2">{fullName}</Typography>
          </Stack>
          <Stack direction={isTablet ? 'column' : 'row'} useFlexGap spacing={1}>
            <Stack useFlexGap direction="row" spacing={1}>
              <WorkOutlineOutlinedIcon />
              <Typography>{publicStaffProfile?.jobTitle ?? '-'}</Typography>
            </Stack>
            <Stack useFlexGap direction="row" spacing={1}>
              <LocationOnOutlinedIcon />
              <Typography>{publicStaffProfile?.locations?.[0]?.name ?? '-'}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Popper>
  )
}

export default MentionsPopper
